import { Button, Card, Flex, Heading, Tabs, Text } from '@chakra-ui/react';
import { createDataCollectionContext, dbTimeToUserTime, useDrawer, useMobileContext } from '@shared';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import inboundNumbersService from '../../api/services/inboundNumbersService';
import SuperadminInboundNumberDrawer from './SuperadminInboundNumberDrawer';
import { useGetAllAccounts } from '../../api/hooks/useAccountSuperadmin';
import { useSearchParams } from 'react-router';
import SuperadminInboundNumberReservations from './SuperadminInboundNumberReservations';

const columnHelper = createColumnHelper<InboundNumber>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function InboundNumbers() {
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<InboundNumber>(),
		[],
	);
	const { t } = useTranslation();
	const { isMobile } = useMobileContext();
	const { openDrawer } = useDrawer();
	const { data: accounts } = useGetAllAccounts();

	const columns = [
		columnHelper.accessor('inboundNumberID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('phone', {
			header: t('general.phone'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text userSelect={'text'}>{row.original.phone}</Text>;
			},
		}),
		columnHelper.accessor('isEnabled', {
			header: t('general.is_enabled'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{row.original.isEnabled === true ? t('general.yes') : t('general.no')}</Text>;
			},
		}),
		columnHelper.accessor('inboundNumberReservations', {
			header: t('general.active_reservation'),
			enableSorting: false,
			cell: ({ row }) => {
				const accountID = row.original.inboundNumberReservations?.find(reservation => reservation.isActive)?.accountID;
				const account = accounts?.data.accounts.find(ac => ac.accountID === accountID)?.name;

				if (!account) {
					<Text>{t('general.no_active_reservation')}</Text>;
				}

				return <Text>{account}</Text>;
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
	];

	const [searchParams, setSearchParams] = useSearchParams();
	const defaultTab = 'inboundNumbers';
	const currentTab = searchParams.get('tab') || defaultTab;

	useEffect(() => {
		if (!searchParams.get('tab')) {
			setSearchParams({ tab: defaultTab }, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	return (
		<Flex flexDirection={'column'} gap={5} width={'100%'} alignItems={'center'} height="100%">
			{!isMobile ? (
				<Card.Root maxWidth="2000px" width="100%">
					<Card.Body>
						<Heading>{t('general.inbound_numbers')}</Heading>
					</Card.Body>
				</Card.Root>
			) : null}

			<Tabs.Root
				maxWidth="2000px"
				variant={'line'}
				size="lg"
				colorPalette={'brand.primary'}
				value={currentTab}
				onValueChange={value => {
					setSearchParams({ tab: value.value });
				}}
				width={'100%'}
				display={'flex'}
				flexDirection={'column'}
				height={'100%'}
				lazyMount
				unmountOnExit
				overflow="auto"
			>
				<Tabs.List flexWrap={'wrap'} display={isMobile ? 'contents' : 'flex'}>
					<Tabs.Trigger value="inboundNumbers" fontWeight={500}>
						{t('general.inbound_numbers')}
					</Tabs.Trigger>
					<Tabs.Trigger value="inboundNumberReservations" fontWeight={500}>
						{t('general.inbound_number_reservations')}
					</Tabs.Trigger>
				</Tabs.List>

				<Tabs.Content value="inboundNumbers" display={'flex'} flex={1} overflow={'auto'}>
					<Card.Root flex={1} display="flex">
						<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
							<DataCollectionContextProvider
								dataKey="inboundNumbers"
								keyAttribute="inboundNumberID"
								emptyDataText={t('general.no_inbound_numbers_found')}
								fetchFn={(...args) => inboundNumbersService.getAllResources(...args)}
								queryParams={{ getRelated: 'inboundNumberReservations' }}
								initialItemsPerPage={25}
								columns={columns}
								defaultHiddenColumns={[]}
								isServerSide={true}
								isSelectable={false}
								defaultSort={{ id: 'timeCreated', desc: true }}
								onClickRow={row => {
									openDrawer({
										id: 'inboundNumberDrawer',
										content: <SuperadminInboundNumberDrawer inboundNumberID={row.original.inboundNumberID} />,
									});
								}}
							>
								<Flex gap={2} flexWrap={'wrap'} ml="auto">
									<DataCollectionTableHeader />
									<Button
										id="addInboundNumber"
										onClick={() => {
											openDrawer({
												id: 'inboundNumberDrawer',
												content: <SuperadminInboundNumberDrawer />,
											});
										}}
									>
										{t('general.add_inbound_number')}
									</Button>
								</Flex>
								<DataCollectionTable />
								<Flex gap={2}>
									<DataCollectionTableFooter />
								</Flex>
							</DataCollectionContextProvider>
						</Card.Body>
					</Card.Root>
				</Tabs.Content>
				<Tabs.Content value="inboundNumberReservations" display={'flex'} flex={1} overflow={'auto'}>
					<SuperadminInboundNumberReservations />
				</Tabs.Content>
			</Tabs.Root>
		</Flex>
	);
}
